"use client";

import { useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import { extractApiErrorMessage } from "../../../utils/error-handler";
import { fetchPlans, disablePlan } from "../../../services/billingService";
import { Button } from "@/components/ui/button";
import { PlusCircle } from "lucide-react";
import PlanList from "./PlanList";
import PlanFormModal from "./PlanFormModal";

const PlansManagement = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);

  const load = async () => {
    try {
      setLoading(true);
      setError("");
      const res = await fetchPlans();
      setPlans(res.data || []);
    } catch (err) {
      setError(extractApiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleDisable = async (plan) => {
    if (!window.confirm(`Disable plan "${plan.name}"? Existing subscriptions on this plan are unaffected.`)) return;
    try {
      await disablePlan(plan._id);
      toast.success("Plan disabled");
      load();
    } catch (err) {
      toast.error(extractApiErrorMessage(err));
    }
  };

  const openCreate = () => {
    setEditingPlan(null);
    setIsModalOpen(true);
  };

  const openEdit = (plan) => {
    setEditingPlan(plan);
    setIsModalOpen(true);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center flex-wrap gap-2">
        <div>
          <h2 className="text-2xl font-bold">Billing Plans</h2>
          <p className="text-sm text-muted-foreground mt-0.5">{plans.length} plan(s).</p>
        </div>
        <Button onClick={openCreate}>
          <PlusCircle className="h-5 w-5 mr-2" />
          Create Plan
        </Button>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
          {error}
        </div>
      )}

      <div className="rounded-2xl border border-gray-100 bg-white shadow-sm overflow-x-auto">
        {loading ? (
          <div className="py-16 text-center text-gray-400 text-sm">Loading...</div>
        ) : (
          <PlanList plans={plans} onEdit={openEdit} onDisable={handleDisable} />
        )}
      </div>

      <PlanFormModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSaved={load}
        plan={editingPlan}
      />
    </div>
  );
};

export default PlansManagement;
